import { Box, Typography, Button } from '@mui/material';
import React, { useMemo, useEffect, useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { metaDataSelector } from '../../store/globalSelectors';
import {
  BMRSection,
  BMRTitle,
  BMRValue
} from '../malnutritionScreening/glim/style';
import { ENRSection } from './enrSection';
import { GridStyled } from './style';
import { NavigationButtonsStyled } from '../../shared/components/navigationButtonsStyled';
import { TileSleekGridGroup } from '../../shared/components/tileSleekGridGroup';
import { pdfSummaryTileData } from '../../shared/shared.constants';
import { useDispatch } from 'react-redux';
import { setSummaryData } from '../../store/slices/metaDataSlice';
import { findMax, displayCalculation } from '../../shared/utils/utilFunctions';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import {
  handleDownloadPdf,
  handleSharePdf,
  roundToPrecision
} from '../../shared/utils/utilFunctions';
//Images
import summaryIcon from '../../assets/icons/summary.svg';
import emailResultsIcon from '../../assets/icons/emailResults.svg';
import downloadIcon from '../../assets/icons/downloadIcon.svg';

export const Summary = () => {
  // For showing a loading state if needed
  const [loading, setLoading] = useState(false);

  const handlePDFGeneration = async (type) => {
    try {
      setLoading(true);
      if (type === 'download') {
        await handleDownloadPdf('Summary.pdf', '', true);
      } else {
        await handleSharePdf('Summary.pdf', 'Summary', '', true);
      }

      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      // console.error(error);
      setLoading(false);
    }
  };

  const dispatch = useDispatch();
  const {
    summary,
    malnutritionScreening,
    muscleScreening,
    patientInformation,
    estimatingRequirements
  } = useAppSelector(metaDataSelector);
  const { age, metric, bmi } = patientInformation;
  const {
    peng2018,
    henryOxford,
    harrisBenedict,
    simpleCaloricEquation,
    faisyEquation,
    pennState,
    modifiedPennState,
    mifflinStJeor,
    protein,
    fluids,
    paediatricEnergy,
    paediatricProtein,
    paedFluid
  } = estimatingRequirements;
  const {
    must,
    mst,
    glim: { phenotypicScore, etiologicalscore }
  } = malnutritionScreening;
  const { sarcf, calfCircumference, handGripStrength } = muscleScreening;

  const calculatedWeight = metric.estimatedDryWeight || metric.weight;
  const ageValue = useMemo(() => {
    if (age.years && !age.months) return age.years;
    else if (!age.years && age.months) return age.months;
    else if (age.years && age.months) return `${age.years}-${age.months}`;
    else return 0;
  }, [age]);
  const ageLabel = useMemo(() => {
    if (!age.years && age.months) return `Age (months):`;
    else if (age.years && age.months) return `Age (years-months):`;
    else return `Age (years)`;
  }, [age]);
  const glimScore = useMemo(() => {
    if (!phenotypicScore && !etiologicalscore) return 0;
    else if (phenotypicScore && !etiologicalscore) return phenotypicScore;
    else if (!phenotypicScore && etiologicalscore) return etiologicalscore;
    else return `${phenotypicScore}-${etiologicalscore}`;
  }, [phenotypicScore, etiologicalscore]);
  const fluidValue = useMemo(() => {
    return roundToPrecision(
      fluids.require1 + fluids.require2 + fluids.fluidLoss,
      1
    );
  }, [fluids.require1, fluids.require2, fluids.fluidLoss]);
  const selectedPengVal = peng2018.reePlusPal || peng2018.ree2ndVal;
  const pengVal =
    selectedPengVal.length > 1
      ? displayCalculation(selectedPengVal)
      : +displayCalculation(selectedPengVal);
  const efsaFluidVal =
    Array.isArray(paedFluid.efsaFluidRequirement) &&
    paedFluid.efsaFluidRequirement.length === 0
      ? 0
      : displayCalculation(paedFluid.efsaFluidRequirement);
  const hoBmrScore = useMemo(() => {
    return henryOxford.bmr > 0 ||
      henryOxford.activityFactor.bmrPlusActivityFactor > 0 ||
      henryOxford.stressFactor.bmrPlusStressFactor > 0 ||
      henryOxford.activityFactorPAL_Select.bmrPlusPALSelect > 0 ||
      henryOxford.activityFactorPAL_Input.bmrPlusPALInput > 0
      ? findMax(
          henryOxford.bmr,
          henryOxford.activityFactor.bmrPlusActivityFactor,
          henryOxford.activityFactorPAL_Select.bmrPlusPALSelect,
          henryOxford.activityFactorPAL_Input.bmrPlusPALInput,
          henryOxford.stressFactor.bmrPlusStressFactor
        ) + henryOxford.summary
      : 0;
  }, [
    henryOxford.bmr,
    henryOxford.activityFactor,
    henryOxford.activityFactorPAL_Select,
    henryOxford.activityFactorPAL_Input,
    henryOxford.stressFactor,
    henryOxford.summary
  ]);

  const hbBmrScore = useMemo(() => {
    return harrisBenedict.bmr > 0 ||
      harrisBenedict.activityFactor.bmrPlusActivityFactor > 0 ||
      harrisBenedict.stressFactor.bmrPlusStressFactor > 0 ||
      harrisBenedict.activityFactorPAL_Select.bmrPlusPALSelect > 0 ||
      harrisBenedict.activityFactorPAL_Input.bmrPlusPALInput > 0
      ? findMax(
          harrisBenedict.bmr,
          harrisBenedict.activityFactor.bmrPlusActivityFactor,
          harrisBenedict.activityFactorPAL_Select.bmrPlusPALSelect,
          harrisBenedict.activityFactorPAL_Input.bmrPlusPALInput,
          harrisBenedict.stressFactor.bmrPlusStressFactor
        ) + harrisBenedict.summary
      : 0;
  }, [
    harrisBenedict.bmr,
    harrisBenedict.activityFactor,
    harrisBenedict.activityFactorPAL_Select,
    harrisBenedict.activityFactorPAL_Input,
    harrisBenedict.stressFactor,
    harrisBenedict.summary
  ]);

  const msjBmrScore = useMemo(() => {
    return mifflinStJeor.bmr > 0 ||
      mifflinStJeor.activityFactor.bmrPlusActivityFactor > 0 ||
      mifflinStJeor.activityFactorPAL_Select.bmrPlusPALSelect > 0 ||
      mifflinStJeor.activityFactorPAL_Input.bmrPlusPALInput > 0
      ? findMax(
          mifflinStJeor.bmr,
          mifflinStJeor.activityFactor.bmrPlusActivityFactor,
          mifflinStJeor.activityFactorPAL_Select.bmrPlusPALSelect,
          mifflinStJeor.activityFactorPAL_Input.bmrPlusPALInput
        ) + henryOxford.summary
      : 0;
  }, [
    mifflinStJeor.bmr,
    mifflinStJeor.activityFactor,
    mifflinStJeor.activityFactorPAL_Select,
    mifflinStJeor.activityFactorPAL_Input,
    mifflinStJeor.summary
  ]);

  useEffect(() => {
    dispatch(
      setSummaryData({
        ...summary,
        age: ageValue,
        weight: calculatedWeight,
        height: metric.height,
        bmi: bmi,
        estimatingRequirements: {
          ...summary.estimatingRequirements,
          peng2018: pengVal,
          henryOxford: hoBmrScore,
          harrisBenedict: hbBmrScore,
          simpleCaloricEquation: {
            ...summary.estimatingRequirements.simpleCaloricEquation,
            min: simpleCaloricEquation.min,
            max: simpleCaloricEquation.max
          },
          faisyEquation: faisyEquation.rmr,
          pennState: pennState.rmr,
          modifiedPennState: modifiedPennState.rmr,
          mifflinStJeor: msjBmrScore,
          protein: {
            ...summary.estimatingRequirements.protein,
            min: protein.calculatedMin,
            max: protein.calculatedMax
          },
          fluids: fluidValue
        },
        paediatricEPF: {
          ...summary.paediatricEPF,
          energyForAge: paediatricEnergy.energyRequirement.energyForAge,
          energyForAgePerKg:
            paediatricEnergy.energyRequirement.energyForAgePerKg,
          energyForWeight: paediatricEnergy.energyRequirement.energyForWeight,
          energyForWeightPerKg:
            paediatricEnergy.energyRequirement.energyForWeightPerKg,
          ar: paediatricProtein.efsa.ar,
          pri: paediatricProtein.efsa.pri,
          ear: paediatricProtein.coma.ear,
          rni: paediatricProtein.coma.rni,
          arPerKg:
            calculatedWeight &&
            +(paediatricProtein.efsa.ar / calculatedWeight).toFixed(2),
          priPerKg:
            calculatedWeight &&
            +(paediatricProtein.efsa.pri / calculatedWeight).toFixed(2),
          earPerKg:
            calculatedWeight &&
            +(paediatricProtein.coma.ear / calculatedWeight).toFixed(2),
          rniPerKg:
            calculatedWeight &&
            +(paediatricProtein.coma.rni / calculatedWeight).toFixed(2),
          fluidMlPerDay: paedFluid.holidaySegar.fluidMlPerDay,
          fluidMlPerKgPerDay: paedFluid.holidaySegar.fluidMlPerKgPerDay,
          efsa: efsaFluidVal,
          iom: paedFluid.iomRequirement
        },
        malnutritionScreening: {
          ...summary.malnutritionScreening,
          must: must.totalScore,
          glim: glimScore,
          mst: mst.totalScore
        },
        muscleScreening: {
          ...summary.muscleScreening,
          sarcf: sarcf.totalScore,
          calfCircumference: calfCircumference.calculatedCircumference,
          handgripStrength: handGripStrength
        }
      })
    );
  }, [
    age,
    metric,
    bmi,
    must,
    mst,
    phenotypicScore,
    etiologicalscore,
    sarcf,
    calfCircumference,
    handGripStrength,
    paediatricEnergy,
    paediatricProtein,
    paedFluid,
    peng2018,
    henryOxford,
    harrisBenedict,
    simpleCaloricEquation,
    faisyEquation,
    pennState,
    modifiedPennState,
    mifflinStJeor,
    protein,
    fluids
  ]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          marginBottom: '1.875rem'
        }}
      >
        <div
          id="section-patient-details"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              width: '110px',
              height: '110px',
              borderRadius: '100px',
              backgroundColor: '#fff',
              marginBottom: '1.25rem',
              marginTop: '1.875rem'
            }}
          >
            <img src={summaryIcon} alt="summaryIcon" />
          </Box>
          <Typography
            sx={{
              fontSize: '1.5625rem',
              lineHeight: '35px',
              fontFamily: 'BGBold',
              fontWeight: '450'
            }}
          >
            SUMMARY
          </Typography>
          <Box>
            <GridStyled container className="width-100">
              <Typography
                sx={{
                  fontSize: '1.125rem',
                  lineHeight: '1.5rem',
                  fontFamily: 'BGBold',
                  fontWeight: '450',
                  textTransform: 'uppercase'
                }}
              >
                Patient Details
              </Typography>
              <BMRSection sx={{ margin: '0px 1.875rem' }}>
                <BMRTitle variant="h2">{ageLabel}</BMRTitle>
                <BMRValue sx={{ width: '100%', textAlign: 'right' }}>
                  {summary.age}
                </BMRValue>
              </BMRSection>
              <BMRSection sx={{ margin: '0px 1.875rem' }}>
                <BMRTitle variant="h2">{'Weight (kg):'}</BMRTitle>
                <BMRValue>{summary.weight}</BMRValue>
              </BMRSection>
              <BMRSection sx={{ margin: '0px 1.875rem' }}>
                <BMRTitle variant="h2">{'Height (cm):'}</BMRTitle>
                <BMRValue>{summary.height}</BMRValue>
              </BMRSection>
              <BMRSection sx={{ margin: '0px 1.875rem' }}>
                <BMRTitle variant="h2">
                  BMI (kg/m
                  <sup style={{ fontSize: '0.6em', verticalAlign: 'super' }}>
                    2
                  </sup>
                  ):
                </BMRTitle>
                <BMRValue>{summary.bmi}</BMRValue>
              </BMRSection>
            </GridStyled>
          </Box>
        </div>

        <Box>
          <ENRSection />
          <Box sx={{ margin: '1.875rem' }}>
            <TileSleekGridGroup tileData={pdfSummaryTileData} />
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={() => handlePDFGeneration('share')}
          data-testid="emailBtbn"
          endIcon={<img src={emailResultsIcon} alt="arrow" />}
          sx={{
            borderRadius: '1.25rem',
            height: '3.125rem',
            margin: '1.25rem',
            background: '#007acc'
          }}
        >
          EMAIL RESULTS
        </Button>
        <Button
          variant="contained"
          onClick={() => handlePDFGeneration('download')}
          data-testid="downloadBtn"
          endIcon={<img src={downloadIcon} alt="downloadIcon" />}
          sx={{
            borderRadius: '1.25rem',
            height: '3.125rem',
            background: '#007acc'
          }}
        >
          DOWNLOAD
        </Button>
      </Box>
      <NavigationButtonsStyled />
      <Backdrop
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#007ACC',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={loading}
      >
        <CircularProgress
          color="inherit"
          size={60}
          thickness={5}
          sx={{ animationDuration: '800ms' }}
        />
        <Typography sx={{ mt: 2 }}>Generating PDF ...</Typography>
      </Backdrop>
    </>
  );
};
